import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useCallback} from 'react'

import type {AccountSwitcherProps} from '../AccountSwitcher'
import type {UserStatus} from '../UserStatusDialog'

export interface LazyLoadItemDataAttributes {
  userStatus: UserStatus
  enterpriseTrialUrl?: string
  hasUnseenFeatures: boolean
  stashedAccounts: AccountSwitcherProps['stashedAccounts']
  showSSO?: boolean
  ssoUrl?: string
  showTeams?: boolean
  teamsUrl?: string
  userPreferences?: {
    tone?: number
  }
}

const erroredLazyLoadItemData: LazyLoadItemDataAttributes = {
  userStatus: {},
  hasUnseenFeatures: false,
  stashedAccounts: [],
}

const LAZY_DATA__DEFAULT_ENDPOINT = '/_side-panels/user.json'

export function useLazyData(url: string = LAZY_DATA__DEFAULT_ENDPOINT) {
  const queryClient = useQueryClient()

  const {data, error, isLoading} = useQuery({
    queryKey: ['global-user-nav-drawer', 'lazy-data', url],
    queryFn: async () => {
      const response = await fetch(url, {credentials: 'include'})
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.statusText}`)
      }
      return response.json() as Promise<LazyLoadItemDataAttributes>
    },
  })

  const updateUserStatus = useCallback(
    (userStatus: UserStatus) => {
      queryClient.setQueryData<LazyLoadItemDataAttributes>(['global-user-nav-drawer', 'lazy-data', url], oldData => {
        if (!oldData) {
          return oldData
        }

        return {
          ...oldData,
          userStatus,
        }
      })
    },
    [queryClient, url],
  )

  return {
    data: error ? erroredLazyLoadItemData : data,
    updateUserStatus,
    error,
    isLoading,
  }
}
